import React, { forwardRef } from "react";
import { useFirebaseContext } from "../firebase/FirebaseContext";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  ChakraProvider,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  MenuItem,
  Link,
  Menu,
  Flex,
  Divider,
  Portal,
} from "@chakra-ui/react";

import { getIsAuthAdmin, getIsAuthLoggedIn } from "../store/selectors";

import Logo from "./icon/svgs/by-black-logo";
import SearchIcon from "./icon/svgs/search-icon";
import HamburgerIcon from "./icon/svgs/hamburger-icon";
import UserMenu from "./user-menu";
import DropdownMenu from "./dropdown-menu";

const certificationLinks = [
  {
    title: "Program Benefits",
    to: "/page/benefits",
  },
  {
    title: "Qualifications",
    to: "/page/qualifications",
  },
  {
    title: "FAQs",
    to: "/page/faq",
  },
  {
    title: "Status of Application",
    to: "/login",
  },
];

const loginLinks = [
  {
    title: "Business Login",
    to: "https://byblack.us/login",
    isExternal: true,
  },
  {
    title: "Certification Login",
    to: "/login",
  },
];

const signupLinks = [
  {
    title: "Business Signup",
    to: "https://byblack.us/signup",
    isExternal: true,
  },
  {
    title: "Certification Signup",
    to: "/register",
  },
];

/** Just get all drawer props together.  */
export const useDrawerInit = () => {
  const history = useHistory();
  const { auth } = useFirebaseContext();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const isLoggedIn = useSelector(getIsAuthLoggedIn);
  const isAdminUser = useSelector(getIsAuthAdmin);

  const onLogout = async () => {
    await auth.signOut();
    history.push("/");
  };

  return {
    isAdminUser,
    isLoggedIn,
    isOpen,
    onClose,
    onLogout,
    onOpen,
  };
};

/** Popout drawer. */
export const PopoutDrawer = forwardRef(function PopoutDrawer(
  { isAdminUser, isLoggedIn, isOpen, onClose, onLogout },
  ref
) {
  return (
    <Portal>
      <Box display={{ base: "block", lg: "none" }}>
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={onClose}
          finalFocusRef={ref}
        >
          <DrawerOverlay />
          <DrawerContent backgroundColor="#112f3a" zIndex={9999}>
            <DrawerCloseButton backgroundColor="#112f3a" color="#ffffff" />

            <DrawerBody color="#ffffff" pt={20}>
              <Menu>
                <MenuItem
                  fontFamily="Moriston"
                  fontWeight={400}
                  as={Link}
                  href="https://byblack.us/about-us"
                  pl={0}
                  my={5}
                >
                  About Us
                </MenuItem>
                <DropdownMenu
                  title="Certification"
                  links={certificationLinks}
                  my={5}
                />

                <MenuItem
                  fontFamily="Moriston"
                  fontWeight={400}
                  as={Link}
                  href="https://byblack.us/partners"
                  pl={0}
                  my={5}
                >
                  Meet our Partners
                </MenuItem>

                <Box w="10%" my={5}>
                  <Divider />
                </Box>

                {isLoggedIn ? (
                  [
                    isAdminUser
                      ? { children: "Admin Dashboard", href: "/dashboard" }
                      : { children: "Go To Application", href: "/login" },
                    { children: "Log Out", onClick: onLogout },
                  ].map((props) => (
                    <MenuItem
                      key={props.href ?? JSON.stringify(props.children)}
                      fontFamily="Moriston"
                      fontWeight={400}
                      as={Link}
                      pl={0}
                      my={5}
                      {...props}
                    />
                  ))
                ) : (
                  <>
                    <DropdownMenu title="Login" links={loginLinks} my={5} />
                    <DropdownMenu title="Sign Up" links={signupLinks} my={5} />
                  </>
                )}
              </Menu>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Box>
    </Portal>
  );
});

export const Navbar = () => {
  const { isAdminUser, isLoggedIn, isOpen, onClose, onLogout, onOpen } =
    useDrawerInit();

  return (
    <ChakraProvider resetCSS={false}>
      <Flex
        boxShadow="none"
        fontFamily="Moriston"
        fontWeight={400}
        color="black"
        backgroundColor="#fff"
        height="65px"
        alignItems="center"
        p={5}
        position="sticky"
        top={0}
        px={25}
        zIndex={1100}
        w="100%"
      >
        <Box flex={{ base: 2, lg: 0.5 }}>
          <Link href="https://byblack.us">
            <Logo />
          </Link>
        </Box>
        <Box display={{ base: "none", lg: "flex" }} flex={3}>
          <Menu>
            <DropdownMenu
              title="Certification"
              links={certificationLinks}
              p={10}
              flex={0.5}
              mt={0}
              position="relative"
              top="-3rem"
            />
            <Flex
              justify="space-round"
              align="center"
              fontSize="16px"
              flex={2.5}
              fontWeight={400}
            >
              <MenuItem
                as={Link}
                fontFamily="Moriston"
                fontWeight={400}
                href="https://byblack.us/about-us"
                pl={0}
                flex={1}
                textDecoration="none"
                _hover={{
                  background: "transparent",
                  color: "#ffb63b",
                  textDecoration: "none",
                }}
                _focus={{
                  color: "#ffb63b",
                  background: "transparent",
                  textDecoration: "none",
                }}
                _active={{
                  color: "#ffb63b",
                  background: "transparent",
                  textDecoration: "none",
                }}
              >
                About Us
              </MenuItem>
              <MenuItem
                as={Link}
                fontFamily="Moriston"
                fontWeight={400}
                href="https://byblack.us/partners"
                pl={0}
                flex={1}
                textDecoration="none"
                _hover={{
                  background: "transparent",
                  color: "#ffb63b",
                  textDecoration: "none",
                }}
                _focus={{
                  color: "#ffb63b",
                  background: "transparent",
                  textDecoration: "none",
                }}
                _active={{
                  color: "#ffb63b",
                  background: "transparent",
                  textDecoration: "none",
                }}
              >
                Meet Our Partners
              </MenuItem>

              {!isLoggedIn && (
                <Flex justify="space-between" align="center" flex={1}>
                  <MenuItem
                    as={Link}
                    fontFamily="Moriston"
                    fontWeight={400}
                    href="/login"
                    pl={0}
                    flex={1}
                    _hover={{
                      background: "transparent",
                      color: "#ffb63b",
                      textDecoration: "none",
                    }}
                    _focus={{
                      color: "#ffb63b",
                      background: "transparent",
                      textDecoration: "none",
                    }}
                    _active={{
                      color: "#ffb63b",
                      background: "transparent",
                      textDecoration: "none",
                    }}
                  >
                    Login
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    fontFamily="Moriston"
                    fontWeight={400}
                    href="/register"
                    pl={0}
                    flex={1}
                    _hover={{
                      background: "transparent",
                      color: "#ffb63b",
                      textDecoration: "none",
                    }}
                    _focus={{
                      color: "#ffb63b",
                      background: "transparent",
                      textDecoration: "none",
                    }}
                    _active={{
                      color: "#ffb63b",
                      background: "transparent",
                      textDecoration: "none",
                    }}
                  >
                    Sign Up
                  </MenuItem>
                </Flex>
              )}
            </Flex>
          </Menu>
        </Box>
        <Flex flex={{ base: 1, lg: 0.3 }} align="center" justify="flex-end">
          <Box display={{ base: "none", lg: "block" }} mr={{ base: 6, md: 8 }}>
            {isLoggedIn && (
              <UserMenu isAdminUser={isAdminUser} logOut={onLogout} />
            )}
          </Box>
          <Link href="https://byblack.us/search" mr={{ base: 6, md: 8 }}>
            <SearchIcon />
          </Link>
          <Box display={{ base: "block", lg: "none" }}>
            <HamburgerIcon onClick={() => onOpen()} />
          </Box>
        </Flex>
        <PopoutDrawer
          {...{
            isAdminUser,
            isLoggedIn,
            isOpen,
            onClose,
            onLogout,
          }}
        />
      </Flex>
    </ChakraProvider>
  );
};

export default Navbar;
